import React from 'react';
import './printlayout.css';

export default function PrintLayout({ relatorios, cidades, igrejas, igrejasDetalhes, cargos }) {

    const getCargoName = (cargoId) => {
        if (!cargoId || !cargos) {
            return 'Cargo';
        }

        if (Array.isArray(cargos)) {
            const cargoEncontrado = cargos.find(c => c.id === cargoId);
            const nome = cargoEncontrado?.nome || 'Cargo';
            return nome;
        }

        if (typeof cargos === 'object') {
            const cargoData = cargos[cargoId];
            const nome = cargoData?.nome || cargoData || 'Cargo';
            return nome;
        }

        return 'Cargo';
    };

    const getLocationName = (igrejaId, location) => {
        if (!igrejaId || !igrejasDetalhes || !igrejasDetalhes[igrejaId]) {
            const defaultNames = {
                'atriu': 'Átrio',
                'corredor': 'Corredor',
                'banheiro': 'Sanitário',
                'estacionamento': 'Estacionamento',
                'som': 'Som'
            };
            return defaultNames[location] || location;
        }

        return igrejasDetalhes[igrejaId][location] || location;
    };

    const groupByMonth = (reports) => {
        const grouped = {};

        reports.forEach((relatorio) => {
            relatorio.report.forEach((entry) => {
                const date = new Date(entry.date);
                date.setHours(date.getHours() + 3);

                const monthYear = `${date.toLocaleString('pt-BR', { month: 'long' })} ${date.getFullYear()}`;
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

                if (!grouped[monthKey]) {
                    grouped[monthKey] = {
                        monthYear: monthYear.charAt(0).toUpperCase() + monthYear.slice(1),
                        igreja: igrejas[relatorio.igreja] || relatorio.igreja,
                        igrejaId: relatorio.igreja,
                        cidade: cidades[relatorio.cidade] || relatorio.cidade,
                        dates: []
                    };
                }

                const cargoName = getCargoName(relatorio.cargo);

                grouped[monthKey].dates.push({
                    date: date,
                    dateFormatted: date.toLocaleDateString('pt-BR'),
                    dayOfWeek: date.toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase().slice(0, 3),
                    time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                    cargo: cargoName,
                    igrejaId: relatorio.igreja,
                    entry: entry
                });
            });
        });

        Object.keys(grouped).forEach(key => {
            grouped[key].dates.sort((a, b) => {
                if (a.date.getTime() !== b.date.getTime()) {
                    return a.date - b.date;
                }
                return a.time.localeCompare(b.time);
            });

            const expandedDates = [];

            grouped[key].dates.forEach(item => {
                let existingDate = expandedDates.find(d =>
                    d.dateFormatted === item.dateFormatted && d.time === item.time
                );

                if (!existingDate) {
                    existingDate = {
                        date: item.date,
                        dateFormatted: item.dateFormatted,
                        dayOfWeek: item.dayOfWeek,
                        time: item.time,
                        igrejaId: item.igrejaId,
                        cargos: []
                    };
                    expandedDates.push(existingDate);
                }

                existingDate.cargos.push({
                    cargo: item.cargo,
                    entry: item.entry
                });
            });

            grouped[key].dates = expandedDates;
        });

        return grouped;
    };

    const calculateDateHeight = (dateItem) => {
        let height = 7;
        height += 2.5;

        dateItem.cargos.forEach(cargoItem => {
            height += 4;

            const locations = ['atriu', 'corredor', 'banheiro', 'estacionamento', 'som'];
            locations.forEach(loc => {
                if (cargoItem.entry[loc] && cargoItem.entry[loc].length > 0) {
                    const people = cargoItem.entry[loc];
                    const namesText = people.map(p => p && p.name ? p.name : '').filter(n => n).join(', ');
                    const estimatedLines = Math.ceil(namesText.length / 30);
                    height += 4 + (estimatedLines > 1 ? (estimatedLines - 1) * 3 : 0);
                }
            });

            height += 2;
        });

        height += 2.5;

        return height;
    };

    const calculateRowHeight = (row) => {
        let maxHeight = 0;
        row.forEach(dateItem => {
            if (dateItem) {
                const height = calculateDateHeight(dateItem);
                if (height > maxHeight) {
                    maxHeight = height;
                }
            }
        });
        return maxHeight;
    };

    const renderPerson = (person) => {
        if (!person || !person.name) return '';
        return person.name;
    };

    const renderLocation = (people) => {
        if (!people || people.length === 0) return 'Não definido';
        return people.map(p => renderPerson(p)).filter(name => name).join(', ') || 'Não definido';
    };

    const renderDateBlock = (dateItem, dateIndex) => {
        if (!dateItem) {
            return <div key={dateIndex} className="date-block-empty"></div>;
        }

        return (
            <div key={dateIndex} className="date-block">
                <div className="date-header">
                    <span className="date-day">{dateItem.date.getDate()}</span>
                    <span className="date-weekday">{dateItem.dayOfWeek}</span>
                    <span className="date-time">- {dateItem.time}h</span>
                </div>
                <div className="date-separator"></div>

                {dateItem.cargos.map((cargoItem, cargoIndex) => (
                    <div key={cargoIndex} className="cargo-block">
                        <div className="cargo-name">{cargoItem.cargo}</div>

                        {cargoItem.entry.atriu && cargoItem.entry.atriu.length > 0 && (
                            <div className="location-line">
                                <span className="location-label">{getLocationName(dateItem.igrejaId, 'atriu')}:</span>
                                <span className="location-people">{renderLocation(cargoItem.entry.atriu)}</span>
                            </div>
                        )}

                        {cargoItem.entry.corredor && cargoItem.entry.corredor.length > 0 && (
                            <div className="location-line">
                                <span className="location-label">{getLocationName(dateItem.igrejaId, 'corredor')}:</span>
                                <span className="location-people">{renderLocation(cargoItem.entry.corredor)}</span>
                            </div>
                        )}

                        {cargoItem.entry.banheiro && cargoItem.entry.banheiro.length > 0 && (
                            <div className="location-line">
                                <span className="location-label">{getLocationName(dateItem.igrejaId, 'banheiro')}:</span>
                                <span className="location-people">{renderLocation(cargoItem.entry.banheiro)}</span>
                            </div>
                        )}

                        {cargoItem.entry.estacionamento && cargoItem.entry.estacionamento.length > 0 && (
                            <div className="location-line">
                                <span className="location-label">{getLocationName(dateItem.igrejaId, 'estacionamento')}:</span>
                                <span className="location-people">{renderLocation(cargoItem.entry.estacionamento)}</span>
                            </div>
                        )}

                        {cargoItem.entry.som && cargoItem.entry.som.length > 0 && (
                            <div className="location-line">
                                <span className="location-label">{getLocationName(dateItem.igrejaId, 'som')}:</span>
                                <span className="location-people">{renderLocation(cargoItem.entry.som)}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const monthsData = groupByMonth(relatorios);
    const sortedMonthKeys = Object.keys(monthsData).sort();

    const allHalves = [];

    sortedMonthKeys.forEach(monthKey => {
        const monthData = monthsData[monthKey];
        let remainingDates = [...monthData.dates];

        while (remainingDates.length > 0) {
            const headerHeight = 18;
            const maxHalfHeight = 138.5;
            const availableHeight = maxHalfHeight - headerHeight;
            const safetyMargin = 7;
            const effectiveHeight = availableHeight - safetyMargin;

            const rows = [];
            let currentHeight = 0;

            while (remainingDates.length > 0) {
                const row = [
                    remainingDates.length > 0 ? remainingDates[0] : null,
                    remainingDates.length > 1 ? remainingDates[1] : null,
                    remainingDates.length > 2 ? remainingDates[2] : null
                ];

                const rowHeight = calculateRowHeight(row);

                if (currentHeight + rowHeight > effectiveHeight && rows.length > 0) {
                    break;
                }

                rows.push(row);
                currentHeight += rowHeight;

                if (remainingDates.length >= 3) {
                    remainingDates = remainingDates.slice(3);
                } else {
                    remainingDates = [];
                }
            }

            allHalves.push({
                rows,
                monthYear: monthData.monthYear,
                igreja: monthData.igreja,
                cidade: monthData.cidade
            });
        }
    });

    const pages = [];
    for (let i = 0; i < allHalves.length; i += 2) {
        pages.push({
            top: allHalves[i],
            bottom: allHalves[i + 1] || null
        });
    }

    return (
        <div id="pdf-content" className="print-container">
            {pages.map((page, pageIndex) => (
                <div key={pageIndex} className="print-page">
                    <div className="print-half">
                        <div className="print-header">
                            <h1>{page.top.monthYear}</h1>
                            <h2>{page.top.igreja} - {page.top.cidade}</h2>
                        </div>

                        <div className="print-rows">
                            {page.top.rows.map((row, rowIndex) => (
                                <div key={rowIndex} className="print-row-wrapper">
                                    <div className="print-row">
                                        {row.map((dateItem, colIndex) => (
                                            <div key={colIndex} className="print-column">
                                                {renderDateBlock(dateItem, colIndex)}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="row-separator"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {page.bottom && (
                        <>
                            <div className="print-divider"></div>

                            <div className="print-half">
                                <div className="print-header">
                                    <h1>{page.bottom.monthYear}</h1>
                                    <h2>{page.bottom.igreja} - {page.bottom.cidade}</h2>
                                </div>

                                <div className="print-rows">
                                    {page.bottom.rows.map((row, rowIndex) => (
                                        <div key={rowIndex} className="print-row-wrapper">
                                            <div className="print-row">
                                                {row.map((dateItem, colIndex) => (
                                                    <div key={colIndex} className="print-column">
                                                        {renderDateBlock(dateItem, colIndex)}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="row-separator"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
}
